// src/utils/tutorialDemos.ts
//
// The demos offered by the first-run tutorial (DemoPickerSplash): every demo in the ground truth
// (groundTruth.ts), labeled with its When, except camera feeds (the tutorial has the user share a
// tab, and an agent watching "my camera" would watch the user's own) and demos still waiting on a
// recording. Each is one of the MCP e2e suite's demo pages (app/e2e/demos/), hosted on the landing
// site at observer-ai.com/demos/ by websites/landing/scripts/sync-showcase.mjs. The tutorial opens
// one in a new tab in tutorial mode (demo-contract.js `?tutorial=`): it holds until the user comes
// back to it after sharing it with Observer, then ends DEMO_RUN_MS later the way its shown pair alerts on. Observer watches it
// like any real screen.
//
// Previews are generated by app/e2e/previews.ts into public/demo-previews/.

import type { TriggerOption } from '@components/AICreator/RecipeSplash';
import { SHOWN, demoFile, isCameraDemo } from './groundTruth.ts';
import { isTauri } from './platform';

export interface TutorialDemo extends TriggerOption {
  /** Demo page file name in e2e/demos/. */
  file: string;
  /** Its tab title, from its When: "My GitHub release fails". */
  title: string;
  /** Full-size screenshot shown above the wheel while this demo is in focus. */
  preview: string;
}

const DEMO_ORIGIN = 'https://observer-ai.com/demos/';
// Long enough to watch it run, short enough not to wait around.
const DEMO_RUN_MS = 30000;

export const TUTORIAL_DEMOS: TutorialDemo[] = SHOWN.filter(u => !isCameraDemo(u) && !u.requires).map(u => {
  const file = demoFile(u);
  return {
    id: file.replace(/\.html$/, ''),
    file,
    title: u.when.charAt(0).toUpperCase() + u.when.slice(1),
    label: u.when,
    promptFragment: u.when,
    sensor: '$SCREEN',
    preview: `/demo-previews/${file.replace(/\.html$/, '.jpg')}`,
  };
});

/** The tab title the demo page sets for itself (see demo-contract.js `?title=`). */
export const demoTabTitle = (d: TutorialDemo): string => `${d.title} · Observer demo`;

export const demoUrl = (d: TutorialDemo): string => {
  // No ?outcome=: the demo ends the way its shown pair alerts on (demo-contract.js data-outcome).
  // The desktop app captures the window natively, so the user never leaves the tab and comes back
  // to start a ?tutorial= hold: there it runs from load with ?autostart= instead.
  const mode = isTauri() ? 'autostart' : 'tutorial';
  const q = new URLSearchParams({ [mode]: String(DEMO_RUN_MS), title: d.title });
  return `${DEMO_ORIGIN}${d.file}?${q}`;
};
